import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authenticate = useCallback(async (initData) => {
    try {
      console.log('🟢 [AuthContext] authenticate called with initData:', initData);
      
      const authUrl = getApiUrl('/api/users/auth/telegram');
      console.log('🟢 [AuthContext] Sending POST to', authUrl);
      
      const response = await fetch(authUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(initData)
      }).catch(fetchError => {
        console.error('❌ [AuthContext] Fetch error:', fetchError);
        console.error('❌ [AuthContext] Fetch error details:', {
          message: fetchError.message,
          name: fetchError.name,
          stack: fetchError.stack
        });
        throw fetchError;
      });

      console.log('🟢 [AuthContext] Response status:', response.status);
      console.log('🟢 [AuthContext] Response ok:', response.ok);
      console.log('🟢 [AuthContext] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [AuthContext] Response not OK:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json().catch(jsonError => {
        console.error('❌ [AuthContext] JSON parse error:', jsonError);
        throw new Error('Failed to parse response as JSON');
      });
      
      console.log('🟢 [AuthContext] Response result:', result);
      
      if (result.success) {
        console.log('✅ [AuthContext] Authentication successful, user:', result.user);
        setUser(result.user);
      } else {
        console.error('❌ [AuthContext] Authentication failed:', result.message);
      }
    } catch (error) {
      console.error('❌ [AuthContext] Auth error:', error);
      console.error('❌ [AuthContext] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        cause: error.cause
      });
      
      // Показываем более понятное сообщение пользователю
      if (error.message.includes('Failed to fetch') || error.message.includes('Load failed')) {
        console.error('❌ [AuthContext] Network error - возможно сервер не запущен или недоступен');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔵 [AuthContext] useEffect triggered - starting authentication');
    
    // Always try to authenticate when app opens
    const performAuth = async () => {
      let initData = {};
      
      // Try to get Telegram WebApp data if available
      if (window.Telegram?.WebApp) {
        console.log('🔵 [AuthContext] Telegram WebApp found');
        const tg = window.Telegram.WebApp;
        tg.ready();
        
        // Try multiple methods to get initData from Telegram WebApp
        initData = tg.initDataUnsafe || {};
        
        // Fallback: try to parse initData string if available
        if (!initData.user && tg.initData) {
          try {
            const params = new URLSearchParams(tg.initData);
            const userStr = params.get('user');
            if (userStr) {
              initData = { user: JSON.parse(decodeURIComponent(userStr)) };
            }
          } catch (e) {
            console.warn('⚠️ [AuthContext] Failed to parse initData string:', e);
          }
        }
      } else {
        console.log('🔵 [AuthContext] Telegram WebApp not found - using empty initData');
      }
      
      console.log('🔵 [AuthContext] initData:', initData);
      console.log('🔵 [AuthContext] initData.user:', initData.user);
      
      // Always call authenticate - backend will handle user creation/update
      console.log('🔵 [AuthContext] Calling authenticate...');
      await authenticate(initData);
    };
    
    // Wait a bit for WebApp to be fully initialized
    setTimeout(performAuth, 200);
  }, [authenticate]);

  const requestAccess = async () => {
    console.log('🟢 [AuthContext] requestAccess вызван');
    console.log('🟢 [AuthContext] user:', user);
    console.log('🟢 [AuthContext] user?.telegramId:', user?.telegramId);
    
    let telegramId = user?.telegramId;
    
    // Если пользователь не авторизован, попробуем получить данные из Telegram WebApp
    if (!telegramId && window.Telegram?.WebApp) {
      console.log('🟡 [AuthContext] Пользователь не авторизован, пытаюсь получить из Telegram WebApp...');
      const tg = window.Telegram.WebApp;
      const initData = tg.initDataUnsafe || {};
      
      if (initData.user) {
        console.log('🟡 [AuthContext] Найден initData.user:', initData.user);
        const tgUser = typeof initData.user === 'string' ? JSON.parse(initData.user) : initData.user;
        telegramId = tgUser.id;
        console.log('🟡 [AuthContext] Извлечен telegramId:', telegramId);
        
        // Попытка авторизоваться
        try {
          console.log('🟡 [AuthContext] Пытаюсь авторизоваться...');
          const response = await fetch(getApiUrl('/api/users/auth/telegram'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(initData)
          });
          
          const result = await response.json();
          console.log('🟡 [AuthContext] Результат авторизации:', result);
          
          if (result.success) {
            setUser(result.user);
            telegramId = result.user.telegramId;
            console.log('✅ [AuthContext] Авторизация успешна!');
          } else {
            console.error('❌ [AuthContext] Авторизация не удалась:', result.message);
          }
        } catch (error) {
          console.error('❌ [AuthContext] Ошибка при авторизации:', error);
        }
      }
    }
    
    // Используем дефолтное значение если telegramId не найден
    if (!telegramId) {
      console.warn('⚠️ [AuthContext] telegramId не найден, используем дефолтное значение');
      telegramId = '0'; // Дефолтное значение для запроса без авторизации
    }

    const requestAccessUrl = getApiUrl('/api/users/request-access');
    console.log('🟢 [AuthContext] Отправляю запрос на сервер...');
    console.log('🟢 [AuthContext] URL:', requestAccessUrl);
    console.log('🟢 [AuthContext] Body:', JSON.stringify({ telegramId: telegramId }, null, 2));

    try {
      const response = await fetch(requestAccessUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ telegramId: telegramId })
      });

      console.log('🟢 [AuthContext] Ответ получен');
      console.log('🟢 [AuthContext] Status:', response.status);
      console.log('🟢 [AuthContext] Status OK:', response.ok);
      
      const result = await response.json();
      console.log('🟢 [AuthContext] Результат:', JSON.stringify(result, null, 2));
      
      if (!response.ok) {
        console.error('❌ [AuthContext] HTTP ошибка:', response.status);
        return { 
          success: false, 
          message: result.message || `Ошибка: ${response.status}` 
        };
      }
      
      console.log('✅ [AuthContext] Успех!');
      return result;
    } catch (error) {
      console.error('❌ [AuthContext] Ошибка сети:', error);
      console.error('❌ [AuthContext] Error message:', error.message);
      console.error('❌ [AuthContext] Error stack:', error.stack);
      return { 
        success: false, 
        message: error.message || 'Ошибка сети при отправке запроса' 
      };
    }
  };

  const refreshAuth = async () => {
    if (window.Telegram?.WebApp) {
      const initData = window.Telegram.WebApp.initDataUnsafe || {};
      if (initData.user) {
        await authenticate(initData);
        return true;
      }
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, loading, requestAccess, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

